import React, { useState, useMemo } from "react";
import {
  Box, Grid, Typography, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Switch, Chip, IconButton, Pagination
} from "@mui/material";
import {
  Search as SearchIcon, EditOutlined, DeleteOutline, Add
} from "@mui/icons-material";

//Modals
import ServicetypeAddModal from "Pages/Modals/AirportTransfer/Create";
import ServicetypeUpdateModal from "Pages/Modals/AirportTransfer/Update";
import ServicetypeDeleteDialog from "Pages/Modals/AirportTransfer/Delete";

// Dummy Data
const INITIAL_DUMMY_DATA = [
  {
    id: "#TR-8821",
    source: { main: "JFK Intl Airport (Terminal 4)", sub: "Queens, NY 11430" },
    destination: { main: "Manhattan Midtown", sub: "West 34th St, NY 10001" },
    serviceType: "Executive Sedan",
    reverse: true,
    status: "Active"
  },
  {
    id: "#TR-8822",
    source: { main: "LAX Intl Airport", sub: "Los Angeles, CA 90045" },
    destination: { main: "Beverly Hills Hotel", sub: "Sunset Blvd, CA 90210" },
    serviceType: "Luxury SUV",
    reverse: false,
    status: "Active"
  },
  {
    id: "#TR-8823",
    source: { main: "ORD Airport", sub: "Chicago, IL 60666" },
    destination: { main: "Downtown Chicago", sub: "Wacker Dr, IL 60601" },
    serviceType: "Premium Sedan",
    reverse: true,
    status: "Inactive"
  },
  {
    id: "#TR-8824",
    source: { main: "DFW Airport", sub: "Dallas, TX 75261" },
    destination: { main: "Uptown Dallas", sub: "McKinney Ave, TX 75201" },
    serviceType: "Economy",
    reverse: false,
    status: "Active"
  },
  {
    id: "#TR-8825",
    source: { main: "SFO Airport", sub: "San Francisco, CA 94128" },
    destination: { main: "Union Square", sub: "Powell St, CA 94102" },
    serviceType: "Executive Sedan",
    reverse: true,
    status: "Active"
  },
];

const AirportTransferFare = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(INITIAL_DUMMY_DATA);
  const rowsPerPage = 5;

  // Modal States
  const [addModal, setAddModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleOpenAdd = () => setAddModal(true);
  const handleCloseAdd = () => setAddModal(false);

  const handleOpenUpdate = (row) => {
    setSelectedRow(row);
    setUpdateModal(true);
  };
  const handleCloseUpdate = () => { setUpdateModal(false); setSelectedRow(null); };

  const handleOpenDelete = (row) => {
    setSelectedRow(row);
    setDeleteModal(true);
  };
  const handleCloseDelete = () => { setDeleteModal(false); setSelectedRow(null); };

  // CRUD Handlers
  const handleAdd = (newData) => {
    const newId = `#TR-${(Math.floor(Math.random() * 9000) + 1000)}`;
    setData([...data, { id: newId, ...newData }]);
    handleCloseAdd();
  };

  const handleUpdate = (updatedData) => {
    setData(data.map(item => item.id === selectedRow.id ? { ...item, ...updatedData } : item));
    handleCloseUpdate();
  };

  const handleDelete = () => {
    setData(data.filter(item => item.id !== selectedRow.id));
    handleCloseDelete();
  };

  const handleToggle = (id) => {
    setData(data.map(item => item.id === id ? { ...item, reverse: !item.reverse } : item));
  };

  const handleExport = (type) => {
    console.log(`${type} clicked`);
  };

  const filteredData = useMemo(() => {
    return data.filter(item =>
      item.id.toLowerCase().includes(search.toLowerCase()) ||
      item.source.main.toLowerCase().includes(search.toLowerCase()) ||
      item.destination.main.toLowerCase().includes(search.toLowerCase()) ||
      item.serviceType.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, data]);

  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 4, boxShadow: "0px 2px 10px rgba(0,0,0,0.05)" }}>
          
          {/* Header */}
          <Grid container justifyContent="space-between" alignItems="center" mb={2}>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center", gap: 2, mb: { xs: 2, md: 0 } }}>
              <Typography variant="h6" fontWeight={800} fontSize={"17px"}>Airport transfer Fare</Typography>
              <Button 
                variant="contained" 
                onClick={handleOpenAdd}
                sx={{ borderRadius: 2, textTransform: "none", padding:"12px 22px", bgcolor: "#000", "&:hover": { bgcolor: "#333" } }} 
                startIcon={<Add />}
              >
                Add Fare Model
              </Button>
            </Grid>
            <Grid item>
              <Typography color="text.secondary">
                Admin / <Typography component="span" color="#000" fontWeight={600}>Airport transfer Fare</Typography>
              </Typography>
            </Grid>
          </Grid>

          {/* Search + Export */}
          <Grid container justifyContent="space-between" alignItems="center" mb={3} spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                sx={{ width: "70%", "& .MuiOutlinedInput-root": { borderRadius: "50px", bgcolor: "#fff" } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#888" }} /></InputAdornment> }}
              />
            </Grid>
            <Grid item>
              <Box sx={{ display: "flex", bgcolor: "#000", padding:"12px 22px", gap: 0, borderRadius: 1, overflow: "hidden" }}>
                {["Copy", "Excel", "CSV", "PDF"].map(btn => (
                  <Button key={btn} size="small" onClick={() => handleExport(btn)}
                    sx={{ color: "#fff", textTransform: "none", px: 2, minWidth: 70, "&:hover": { bgcolor: "#222" } }}>
                    {btn}
                  </Button>
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "#F8F9FC" }}>
                <TableRow>
                  {["ID", "Source Address", "Destination Address", "Service Type", "Reverse", "Status", "Action"].map(head => (
                    <TableCell key={head} align="center" sx={{ fontWeight: 800, fontSize: "15px", whiteSpace: "nowrap" }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>{row.id}</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={500}>{row.source.main}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.source.sub}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={500}>{row.destination.main}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.destination.sub}</Typography>
                    </TableCell>
                    <TableCell align="center">{row.serviceType}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <Switch
                          checked={row.reverse}
                          onChange={() => handleToggle(row.id)}
                          sx={{
                            width: 46, height: 24, padding: 0,
                            "& .MuiSwitch-switchBase": { padding: "2px", "&.Mui-checked": { transform: "translateX(20px)", color: "#fff", "& + .MuiSwitch-track": { backgroundColor: "#4CD964", opacity: 1 } } },
                            "& .MuiSwitch-thumb": { width: 20, height: 20 },
                            "& .MuiSwitch-track": { borderRadius: 20, backgroundColor: "#FF4D4F", opacity: 1 },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">{row.reverse ? "YES" : "NO"}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={row.status} 
                        size="small" 
                        sx={{ bgcolor: row.status === "Active" ? "#c3e3c3" : "#FFEBEE", color: row.status === "Active" ? "#1ca523" : "#C62828", borderRadius: 4, fontWeight: 700, padding: "5px 10px" }} 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" sx={{ color: "#555" }} onClick={() => handleOpenUpdate(row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: "#d32f2f" }} onClick={() => handleOpenDelete(row)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No results found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Pagination */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, flexWrap: "wrap", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredData.length === 0 ? 0 : ((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, filteredData.length)} of {filteredData.length} results
            </Typography>
            <Pagination 
              count={Math.ceil(filteredData.length / rowsPerPage)} 
              page={page} 
              onChange={(e, val) => setPage(val)}
              shape="rounded"
              sx={{ "& .Mui-selected": { bgcolor: "#000 !important", color: "#fff" } }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Modals */}
      <ServicetypeAddModal
        openDialog={addModal}
        handleClose={handleCloseAdd}
        onSubmit={handleAdd}
      />
      <ServicetypeUpdateModal
        openDialog={updateModal}
        handleClose={handleCloseUpdate}
        data={selectedRow}
        onSubmit={handleUpdate}
      />
      <ServicetypeDeleteDialog
        openDialog={deleteModal}
        handleClose={handleCloseDelete}
        data={selectedRow}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default AirportTransferFare;